import { DiscountStance } from '../../lib/types';

export const DISCOUNT_STANCE_META: Record<
  DiscountStance,
  { label: string; dot: string; bg: string; ink: string; tooltip: string }
> = {
  open: {
    label: 'Discounts OK',
    dot: '#1f9d55',
    bg: '#e7f6ec',
    ink: '#0d7a36',
    tooltip: 'Discounts OK — no need to ask the brand.',
  },
  negotiate: {
    label: 'Negotiate',
    dot: '#d97706',
    bg: '#fff4e0',
    ink: '#9a5400',
    tooltip:
      'Negotiate — campaign-only discounts, and the brand must be informed first.',
  },
  restricted: {
    label: 'No discounts',
    dot: '#c83333',
    bg: '#fde7e7',
    ink: '#8b1d1d',
    tooltip:
      "Restricted — the brand doesn't allow discounts. Only with a pre-agreed exception.",
  },
};

interface DotProps {
  stance: DiscountStance;
  size?: number;
  title?: string;
}

/** Tiny coloured dot — for dense rows (partnership tracking list). */
export const DiscountStanceDot = ({ stance, size = 9, title }: DotProps) => {
  const meta = DISCOUNT_STANCE_META[stance];
  return (
    <span
      title={title ?? meta.tooltip}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: meta.dot,
        flexShrink: 0,
      }}
    />
  );
};

interface ChipProps {
  stance: DiscountStance;
  /** Drop the label and just show a coloured dot in a chip-shaped pill. */
  compact?: boolean;
  onClick?: () => void;
  title?: string;
}

/** Label chip for the brand detail page. Clickable when `onClick` is set. */
export const DiscountStanceChip = ({
  stance,
  compact = false,
  onClick,
  title,
}: ChipProps) => {
  const meta = DISCOUNT_STANCE_META[stance];
  const interactive = !!onClick;
  return (
    <span
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={(e) => {
        if (!interactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      title={title ?? meta.tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: compact ? '2px 6px' : '3px 10px',
        borderRadius: 999,
        background: meta.bg,
        color: meta.ink,
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: meta.dot,
        }}
      />
      {!compact && meta.label}
    </span>
  );
};

export default DiscountStanceChip;
